import { Button, Theme, Tooltip } from '@mui/material';

import AppBar from '@mui/material/AppBar';
import AuthService from '../../../shared/services/AuthService';
import BackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import CommentIcon from '@mui/icons-material/Comment';
import DesktopSelectedImage from '../../images/desktop_selected.svg';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import LessonPlayerMobilePreview from './LessonPlayerMobilePreview';
import { LessonPlayerProps } from './LessonPlayer';
import { LessonPlayerUsersPanel } from './LessonPlayerUsersPanel';
import LessonService from '../../services/LessonService';
import { LessonTitleInfo } from '../LessonTitleInfo';
import MobileDeselectedImage from '../../images/mobile_deselected.svg';
import React from 'react';
import { ReactSVG } from 'react-svg';
import Toolbar from '@mui/material/Toolbar';
import { styled } from '@mui/system';
import styles from '../../styles/LessonPlayer.scss';
import { useExternalLessonContext } from '../../../dashboard/models/ExternalLessonContext';
import { useLessonPlayerMarkingContext } from '../../../assessments/models/LessonPlayerMarkingContext';
import useMobilePreview from './useMobilePreview';

export interface LessonPlayerAppBarProps extends LessonPlayerProps {
  setShowCommentPanel: React.Dispatch<React.SetStateAction<boolean>>;
}

const DesktopSelectedIcon = () => <ReactSVG src={DesktopSelectedImage} />;
const MobileDeselectedIcon = () => <ReactSVG src={MobileDeselectedImage} />;

const StyledBox = styled(Box)(({ theme }) => ({
  '& [disabled]': { opacity: 0.5 },
  '& svg': { width: 32, height: 32 },
  '& svg path': { fill: theme.palette.primary.contrastText }
}));

function LessonPlayerAppBar(props: LessonPlayerAppBarProps) {
  const { assignedToMe } = useLessonPlayerMarkingContext();
  const { previewLink, setMobilePreview } = useMobilePreview(props.lessonkey);
  const framed = window.parent !== window;
  const showMobilePreviewControls = !!AuthService.User;
  const { isExternalLesson } = useExternalLessonContext();
  const handleClose = () => {
    if (framed) {
      window.parent?.postMessage({ action: 'onClose' });
    } else {
      props.onClose();
    }
  };

  return (
    <AppBar className={styles.appBar}>
      <Toolbar>
        {!isExternalLesson && (
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <BackIcon />
          </IconButton>
        )}
        <Grid
          container
          alignItems="center"
          spacing={2}
          wrap="nowrap"
          width={'100%'}
        >
          <LessonTitleInfo
            displaySettings={props.displaySettings}
            lessonTitle={props.lessonTitle}
            lessonDescription={props.lessonDescription}
            timeToComplete={props.timeToComplete}
          />
          {props.displayMode === 'preview' && showMobilePreviewControls && (
            <StyledBox>
              <IconButton disabled color="primary">
                <DesktopSelectedIcon />
              </IconButton>
              <Tooltip title="Show mobile preview">
                <IconButton
                  color="inherit"
                  onClick={() => setMobilePreview(true)}
                >
                  <MobileDeselectedIcon />
                </IconButton>
              </Tooltip>
            </StyledBox>
          )}
          {props.displayMode === 'mark' && (
            /* Hide marker and learner details on mobile view and small screens */
            <Box display={{ xs: 'none', sm: 'none', md: 'block' }}>
              <Grid>
                <LessonPlayerUsersPanel
                  learner={props.learner}
                  marker={props.marker}
                />
              </Grid>
            </Box>
          )}
          {/* CommentPanel should be visible one of these conditions:
            1. For any marker or reviewer, it is visible in all states after assigning.
               But only in 'Marking started' state, they can add/edit or delete comments.
            2. For learners, it is always readonly in assessment lock state and work required state
            3. For Master admin, the comment panel is disabled if they open any assessment
               which is assigned to somebody else */}
          {LessonService.showCommentMenu(props.displayMode, props.state) && (
            <Grid>
              <IconButton
                size="small"
                disabled={LessonService.disableCommentMenu(
                  props.displayMode,
                  assignedToMe
                )}
                onClick={() => props.setShowCommentPanel((s) => !s)}
              >
                <CommentIcon
                  className={
                    LessonService.disableCommentMenu(
                      props.displayMode,
                      assignedToMe
                    )
                      ? ''
                      : styles.commentIcon
                  }
                />
              </IconButton>
            </Grid>
          )}
        </Grid>
        {isExternalLesson && (
          <Button color="inherit" onClick={handleClose}>
            Close lesson
          </Button>
        )}
      </Toolbar>
      {previewLink && (
        <LessonPlayerMobilePreview
          link={previewLink}
          onClose={() => setMobilePreview(false)}
        />
      )}
    </AppBar>
  );
}

export default LessonPlayerAppBar;
